import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, RouteReuseStrategy, withHashLocation } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideLottieOptions, provideCacheableAnimationLoader } from 'ngx-lottie';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { CustomRouteReuseStrategy } from './core/route-reuse/custom-route-reuse-strategy';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAnimations(),

        // Lottie (ngx-lottie) — plays After Effects / LottieFiles JSON animations.
        // Loads lottie-web lazily and caches parsed animations across component instances.
        provideLottieOptions({ player: () => import('lottie-web') }),
        provideCacheableAnimationLoader(),

        provideRouter(
            appRoutes,
            withHashLocation(), // Enable hash routing to match Angular 11 URL structure (#/resource-view)
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Snackbars without `panelClass` otherwise use Material grey — default to fiery gradient.
        // Use firefly-snackbar (not fiery-snackbar) so long messages are not faded out mid-display.
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['firefly-snackbar'],
            },
        },

        // Custom Route Reuse Strategy - Cache list pages
        {
            provide: RouteReuseStrategy,
            useClass: CustomRouteReuseStrategy,
        },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'es',
                        label: 'Español',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang)).catch((error) => {
                console.error('Failed to load translation file:', error);
                // Return empty object to prevent app initialization from failing
                return {};
            });
        }),

        // Service Worker — registers `ngsw-worker.js` under Angular's
        // control so `SwUpdate` receives version events. Disabled in
        // dev so `ng serve` (`isDevMode() === true`) never installs a
        // SW that would then cache the dev bundle. The 30-second
        // stability delay defers registration past app bootstrap so
        // it doesn't compete with first-paint network work.
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'dense',
                scheme: 'dark',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
